import axios from 'axios';
import { endpoints } from '@api/apis';
import { store } from '@store/newstore';
import { trackEvent } from '@app/utils';
import { setFeatureFlagVariant } from '@store/slices/featureFlagSlice';
import type {
  FeatureFlagTraits,
  FeatureFlagVariantResponse,
  ResolveFeatureFlagArgs,
  ResolveFeatureFlagResult,
} from './types';

/** Pull the current user's id from auth state — used only as the cache key. */
export const getCurrentUserId = (): string | undefined => {
  const userDetails = store.getState()?.auth?.userDetails;
  return userDetails?.user?.userId ?? undefined;
};

/** Read a cached variant straight from the redux store for a user + flag. */
const getCachedVariant = (userId: string, featureFlagName: string) => {
  const featureFlags = store.getState()?.featureFlags;
  if (!featureFlags || featureFlags.userId !== userId) return null;
  return featureFlags.variants[featureFlagName] ?? null;
};

/**
 * Resolve a feature-flag variant with a three-tier strategy:
 *   1. Hit the API. On success, cache the variant (keyed by userId) and use it.
 *   2. On failure, fall back to the cached variant for this user + flag.
 *   3. If both miss, return the caller-supplied `defaultVariant`.
 *
 * The call goes through the global axios with `handle500Here: true`, so a 500
 * never triggers the global error sheet — A/B resolution fails silently and
 * falls back to cache/default. The endpoint is also in `encryptionSkipUrls`
 * (see interceptor) so the body is sent/received as plain JSON.
 *
 * The call never throws; it always resolves to a usable variant string.
 */
export const resolveFeatureFlagVariant = async ({
  featureFlagName,
  defaultVariant,
  traits,
  userId,
}: ResolveFeatureFlagArgs): Promise<ResolveFeatureFlagResult> => {
  const effectiveUserId = userId ?? getCurrentUserId();

  // Traits are fully caller-controlled and optional (default: empty object).
  const effectiveTraits: FeatureFlagTraits = traits ?? {};

  // ---- 1) Try the API ----
  try {
    const res = await axios.post<FeatureFlagVariantResponse>(
      endpoints.getFeatureFlagVariant,
      { featureFlagName, traits: effectiveTraits },
      { handle500Here: true },
    );

    // Response shape: { status: '<variant>', id: '<some-id>' }.
    // The A/B variant itself is in `status`; `id` is an extra identifier.
    const data = res?.data;
    const variantName = data?.status || undefined;
    if (variantName) {
      // Cache only when we have a user to own the entry.
      if (effectiveUserId) {
        store.dispatch(
          setFeatureFlagVariant({
            userId: effectiveUserId,
            featureFlagName,
            variant: {
              variantName,
              id: data?.id ?? undefined,
              traits: effectiveTraits,
            },
          }),
        );
      }
      return { variant: variantName };
    }
    // API responded but without a usable variant — treat as a miss.
    trackEvent('FEATURE_FLAG_API_EMPTY_VARIANT', { featureFlagName });
  } catch (err) {
    trackEvent('FEATURE_FLAG_API_FAILED', {
      featureFlagName,
      error: (err as Error)?.message,
    });
  }

  // ---- 2) Fall back to cache (redux) ----
  if (effectiveUserId) {
    const cached = getCachedVariant(effectiveUserId, featureFlagName);
    if (cached?.variantName) {
      return { variant: cached.variantName };
    }
  }

  // ---- 3) Default ----
  // Both API and cache missed. Persist the default into redux (so the slice is
  // always populated for this user + flag) and then return it. The hook keeps
  // its loader on until this resolves, so the consumer never renders a variant
  // mid-flight. Gated on userId because the cache is keyed by user.
  if (effectiveUserId) {
    store.dispatch(
      setFeatureFlagVariant({
        userId: effectiveUserId,
        featureFlagName,
        variant: {
          variantName: defaultVariant,
          traits: effectiveTraits,
        },
      }),
    );
  }
  return { variant: defaultVariant };
};

/**
 * Convenience wrapper that returns just the variant string.
 * Use this when you don't care where the value came from.
 */
export const getFeatureFlagVariant = async (
  args: ResolveFeatureFlagArgs,
): Promise<string> => {
  const { variant } = await resolveFeatureFlagVariant(args);
  return variant;
};

/**
 * Synchronous read of a cached variant from the redux store — does NOT hit
 * the API. Pair with an upstream `resolveFeatureFlagVariant` (or
 * `useFeatureFlag`) call earlier in the flow so the cache is already
 * populated when downstream consumers read it.
 *
 * Falls back to `defaultVariant` when the cache has no entry, or when it
 * belongs to a different user (handled by `getCachedVariant`).
 *
 * Snapshot semantics: this reads `store.getState()` once at call time. Callers
 * won't re-render if the cache changes later — which is the desired UX,
 * because a mid-render variant flip would visibly flicker the screen.
 */
export const getCachedFeatureFlagVariant = (
  featureFlagName: string,
  defaultVariant: string,
): string => {
  const userId = getCurrentUserId();
  if (!userId) return defaultVariant;
  const cached = getCachedVariant(userId, featureFlagName);
  return cached?.variantName ?? defaultVariant;
};
